import { Button, Label } from 'flowbite-react';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { MultiValue } from 'react-select';
import Select from 'react-select';
import { Dropdown, allSkillsDropdownForPosition, locationPreferenceOptions } from 'shared-utils';
import { getMyRegisteredPositions } from 'src/apis/position';
import { useToast } from 'src/context/ToastProvider';
import { FilterPositionCompanyInput } from 'types';
import { PrettoSlider } from '../RecruiterCandidatesFilters';
import { useRecommendationsContext } from 'src/context/RecommendationsProvider';
import { get } from 'lodash';
import { useUserContext } from 'src/context/UserProvider';

const PostionFiltersForm = ({
	companyId,
	onClose,
	onApply,
	onClear,
}: {
	companyId?: string;
	onClose?: () => void;
	onApply?: () => void;
	onClear?: () => void;
}) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const { isRecruiterLoggedIn } = useUserContext();
	const [filters, setFilters] = React.useState<(FilterPositionCompanyInput & { companyId?: string }) | null>();
	const [value, setValue] = React.useState<MultiValue<Dropdown>>([]);
	const [skills, setSkills] = React.useState<MultiValue<Dropdown>>([]);
	const { setIsFilterLoading } = useRecommendationsContext();

	const filterCandidatesFn = useMutation(['getMyRegisteredPositions'], getMyRegisteredPositions, {
		onSuccess(data) {
			setIsFilterLoading(false);
			queryClient.setQueryData(['getMyRegisteredPositions', companyId], data);
			onClose && onClose();
		},
		onError(error: any) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			setIsFilterLoading(false);
			toast({
				title: errorMessage,
				type: 'error',
				duration: 5000,
			});
		},
	});

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				setIsFilterLoading(true);
				if (isRecruiterLoggedIn || companyId?.length)
					filterCandidatesFn.mutate({
						...filters,
						companyId: companyId || '',
						skills: JSON.stringify(skills.map(s => s.value)),
						locations: JSON.stringify(value.map(s => s.value)),
					});
			}}
		>
			<div className="mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label htmlFor="skill" className="font-[700] font-inter text-sm text-[#28293D]" value="Skill required" />
				</div>

				<Select
					isClearable
					isMulti
					options={allSkillsDropdownForPosition}
					onChange={(vals: MultiValue<Dropdown>) => {
						setSkills(vals);
						setFilters(prev => {
							return { ...prev };
						});
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="MERN, JAVA"
					blurInputOnSelect={false}
					closeMenuOnSelect={false}
					value={skills}
				/>
			</div>

			<div className="mt-[28px]">
				<div className="mb-4 block">
					<Label
						htmlFor="expectedSalary"
						className="font-[700] font-inter text-sm text-[#28293D]"
						value="Position salary"
					/>
				</div>

				<PrettoSlider
					valueLabelDisplay="on"
					getAriaLabel={() => 'Position Salary'}
					max={80}
					min={1}
					// // getAriaLabel={index => (index === 0 ? 'Minimum Position Salary' : 'Maximum Position Salary')}
					defaultValue={[12, 37]}
					onChange={(e: Event, newValue: number | number[]) => {
						if (!Array.isArray(newValue)) {
							return;
						}
						setFilters(prev => {
							return { ...prev, salaryRange: { min: newValue[0], max: newValue[1] } };
						});
					}}
				/>

				<div className="flex justify-between text-sm text-[#555770]  mt-[-10px]">
					<p>1 LPA</p>
					<p>80 LPA</p>
				</div>
			</div>

			<div className="mt-[28px]">
				<div className="mb-4 block">
					<Label htmlFor="level" className="font-[700] font-inter text-sm text-[#28293D]" value="Candidate Level" />
				</div>

				<PrettoSlider
					valueLabelDisplay="on"
					getAriaLabel={() => 'Level'}
					max={1000}
					min={2}
					// // getAriaLabel={index => (index === 0 ? 'Minimum level' : 'Maximum level')}
					defaultValue={[305, 505]}
					onChange={(e: Event, newValue: number | number[]) => {
						if (!Array.isArray(newValue)) {
							return;
						}
						setFilters(prev => {
							return { ...prev, levelRange: { min: newValue[0], max: newValue[1] } };
						});
					}}
				/>

				<div className="flex justify-between text-sm text-[#555770] mt-[-10px]">
					<p>2</p>
					<p>1000</p>
				</div>
			</div>

			<div className="mt-[20px] whiteBgSelect">
				<div className="mb-4 block">
					<Label htmlFor="location" className="font-[700] font-inter text-sm text-[#28293D]" value="Location" />
				</div>

				<Select
					isClearable
					isMulti
					options={locationPreferenceOptions.map(l => ({ label: l, value: l }))}
					onChange={(vals: MultiValue<Dropdown>) => {
						setValue(vals);
						setFilters(prev => {
							return { ...prev };
						});
					}}
					menuPlacement="auto"
					className="[&>*]:!min-h-[46px]"
					classNamePrefix="my-select-plans"
					placeholder="Mumbai, Bangalore"
					blurInputOnSelect={false}
					closeMenuOnSelect={false}
					value={value}
				/>
			</div>

			<div className="flex flex-wrap gap-[20px] mt-[17px]">
				<Button
					type="reset"
					color="light"
					disabled={filterCandidatesFn.isLoading}
					className="flex-1"
					onClick={() => {
						setValue([]);
						setSkills([]);
						setFilters(null);
						setIsFilterLoading(true);
						if (isRecruiterLoggedIn || companyId?.length) filterCandidatesFn.mutate({ companyId: companyId || '' });
						onClear && onClear();
					}}
				>
					Clear
				</Button>
				<Button
					disabled={filterCandidatesFn.isLoading}
					isProcessing={filterCandidatesFn.isLoading}
					type="submit"
					color="purplePlaced"
					className="flex-1 bg-[#7777E7]"
					onClick={onApply}
				>
					Apply
				</Button>
			</div>
		</form>
	);
};

export default PostionFiltersForm;
